import { DEFAULT_API_ADMIN } from '../../../constants';
import * as authManager from '../../../lib/authManager';
import { AppArtBucket, AppArtFile } from '../types';

/**
 * Download de verdade — o arquivo cai na pasta de downloads.
 *
 * Não dá para apontar um `<a download href={fileUrl}>` direto para o S3: o
 * atributo `download` é ignorado quando a origem é outra, então o clique só
 * abria a imagem numa aba. E `fetch` na URL do bucket também não resolve,
 * porque o bucket não tem CORS ("CORS is not enabled for this bucket").
 *
 * Por isso o binário vem pela API, que é mesma origem do resto do admin, já
 * responde com CORS liberado e manda Content-Disposition: attachment.
 *
 * É `fetch` cru, e não o dataProvider, porque o dataProvider (e o
 * `httpClientReactAdmin`, que é `fetchJson`) só sabem falar JSON: aplicados a
 * um PNG ou a um zip, quebram no parse e perdem o Content-Disposition. A
 * exceção fica confinada aqui — os componentes chamam estas duas funções, não
 * a API.
 */

const authHeaders = async () => {
  const token = await authManager.ensure();
  return { Authorization: token || '' };
};

/** Nome do arquivo que o servidor mandou no Content-Disposition. */
const fileNameFromResponse = (response: Response, fallback: string) => {
  const disposition = response.headers.get('content-disposition') || '';
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try { return decodeURIComponent(encoded[1]); } catch { /* cai no plain */ }
  }
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
};

/** Salva um blob com o gesto de clique — é o que dispara a caixa de download. */
export const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revogar na hora cancela o download no Safari; um tick depois é seguro.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

const fetchAsAttachment = async (url: string, fallbackName: string) => {
  const response = await fetch(url, { headers: await authHeaders() });
  if (!response.ok) {
    const message = await response.text().catch(() => '');
    let detail = '';
    try { detail = JSON.parse(message)?.message || ''; } catch { detail = ''; }
    throw new Error(detail || `Falha ao baixar (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  saveBlob(blob, fileNameFromResponse(response, fallbackName));
  return response;
};

/** Baixa um arquivo do material. */
export const downloadArtFile = async (file: AppArtFile) => {
  const fallback = file.fileName || `${file.bucket}-${file.id}`;
  await fetchAsAttachment(`${DEFAULT_API_ADMIN}/app-art-files/${file.id}/download`, fallback);
};

/**
 * Baixa o material num zip só, montado pela API.
 *
 * O empacotamento é do servidor pelo mesmo motivo do download avulso: sem CORS
 * no bucket, o browser não lê os binários para juntar. E o zip sai organizado
 * por pasta (`logos/`, `imagens-do-posto/`, `modelos/rodada-N/`), com o
 * principal e o escolhido marcados no nome — estrutura que se perde quando os
 * arquivos descem soltos.
 */
export const downloadArtBundle = async (
  chainId: string,
  options: { bucket?: AppArtBucket; chainName?: string } = {},
) => {
  const query = options.bucket ? `?bucket=${encodeURIComponent(options.bucket)}` : '';
  const response = await fetchAsAttachment(
    `${DEFAULT_API_ADMIN}/app-art/${chainId}/download${query}`,
    `arte-${options.chainName || chainId}.zip`,
  );

  // Arquivo que sumiu do bucket não derruba o pacote — mas quem baixou precisa
  // saber que o zip veio incompleto.
  return { missing: Number(response.headers.get('x-missing-files') || 0) };
};

/**
 * O binário do arquivo, buscado pela API.
 *
 * Pela API e não pelo bucket porque o bucket não tem CORS — `fetch` nele nem
 * chega a responder. Pela API é mesma origem, e o que volta pode ser desenhado
 * ou embutido à vontade.
 *
 * O QUE se busca é a mesma regra do mockup ao vivo (`previewUrl || fileUrl`):
 * logo em PDF/AI não renderiza em `<img>`, o que existe dela é o preview PNG.
 * Sem o `variant=preview` a captura embutia `data:application/pdf` e a logo
 * saía QUEBRADA no PNG — visível no header da home, e invisível no welcome só
 * porque lá o `<img>` tem `height: auto` e colapsa.
 */
const artFileBlob = async (file: AppArtFile): Promise<Blob> => {
  const variant = file.previewUrl ? '?variant=preview' : '';
  const response = await fetch(
    `${DEFAULT_API_ADMIN}/app-art-files/${file.id}/download${variant}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) { throw new Error(`Falha ao ler ${file.fileName || file.id}`); }

  const raw = await response.blob();
  // SVG subido antes de `resolveMimeType` existir ficou gravado como
  // `application/xml` (o sniff do backend não acha magic bytes em texto), e
  // com esse mime o `<img>` recusa desenhar. O byte está certo, só o rótulo
  // não — e sem preview PNG para cair, corrigir o rótulo é o que resta.
  const isSvg = (file.fileName || '').toLowerCase().endsWith('.svg');
  return isSvg && !raw.type.startsWith('image/')
    ? new Blob([raw], { type: 'image/svg+xml' })
    : raw;
};

/**
 * O arquivo como data URI, para poder ser RASTERIZADO junto com o DOM.
 *
 * A captura do mockup desenha a árvore num canvas, e canvas com imagem de outra
 * origem fica "sujo": o `toBlob` estoura por segurança. Embutido como data URI,
 * o `html-to-image` leva a imagem junto e a captura funciona.
 */
export const artFileAsDataUri = async (file: AppArtFile): Promise<string> => {
  const blob = await artFileBlob(file);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo'));
    reader.readAsDataURL(blob);
  });
};

/**
 * O arquivo pronto para ser DESENHADO num canvas.
 *
 * Quem compõe em canvas não precisa do data URI: o binário vem da API, que é
 * mesma origem, então desenhá-lo não suja o canvas — e pular o base64 evita
 * inflar em um terço um gabarito que vai ser desenhado dezenas de vezes.
 *
 * `createImageBitmap` decodifica de uma vez e entrega algo que o `drawImage`
 * consome sem redecodificar a cada placa. SVG sem dimensão intrínseca não passa
 * por ele em todo browser; aí vale o `<img>`, que aceita o mesmo blob.
 */
export const artFileAsImage = async (file: AppArtFile): Promise<ImageBitmap | HTMLImageElement> => {
  const blob = await artFileBlob(file);
  try {
    return await createImageBitmap(blob);
  } catch {
    const url = URL.createObjectURL(blob);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      return image;
    } finally {
      // Revogar depois do `decode` é seguro: o bitmap já está na imagem.
      URL.revokeObjectURL(url);
    }
  }
};

/** Salva um data URI como arquivo. */
export const saveDataUrl = async (dataUrl: string, fileName: string) => {
  // Anchor com data: URI de vários MB trava em alguns browsers; blob não.
  const blob = await (await fetch(dataUrl)).blob();
  saveBlob(blob, fileName);
};
